import { DateTime } from "luxon";
import type { ResolvedActualDay } from "../../components/StaffTable/utils";
import type { Department } from "../../contexts/AuthProvider";
import type {
	CalledOffShift,
	CensusPointInTimes,
	ChangesSource,
	CustomTimeGroupsConfig,
	DayStats,
	HospitalData,
	LocationMode,
	NurseFullRole,
	NurseInShift,
	PrintoutShowAdminsConfig,
} from "../../utils/models";
import { createBuildContext } from "./aiPrintoutBuildContext";
import type { FindDepartmentLocation } from "./aiPrintoutCensusGroups";
import { buildDayExtras, buildDayMetrics, buildRows } from "./aiPrintoutDayParts";
import { type AiPrintoutData, type AiPrintoutLabels, CONTRACT_VERSION } from "./contract";

export type BuildAiPrintoutDataInput = {
	/** The schedule day being viewed. */
	date: DateTime;
	isActual: boolean;
	/** The day's scheduled shifts as the Day view holds them, already assigned to this schedule day. */
	scheduledShifts: NurseInShift[];
	/**
	 * The day's actual side as the Day view's actual mode resolves it (`useResolvedActualDay`): punches, scheduled
	 * shifts standing in for staff who don't clock in, and scheduled shifts nobody punched for.
	 */
	actualDay: ResolvedActualDay;
	/** The Day view's stats for each side (`weekViewData`'s `dayStats` / `dayStatsActual`, by `calculateDayStats`). */
	dayStats: DayStats;
	dayStatsActual: DayStats;
	ptoShifts: NurseInShift[];
	callOuts: CalledOffShift[];
	hospitalData: HospitalData;
	weekIndex: number;
	department: Department;
	jobManagementEnabled: boolean;
	lastNameFirst: boolean;
	/** `!isHospital` and the department's roles: who counts toward hours, as `calculateDayStats` decides. */
	isSnf: boolean;
	rolesToDisplay: NurseFullRole[];
	/** The Day view's `findDepartmentLocationForNurse`, which places shifts in census groups. */
	findDepartmentLocation: FindDepartmentLocation;
	/** The viewer's manual location order (`useGlobalManualLocationOrder`), null when they haven't set one. */
	customLocationOrder: Map<string, number> | null;
	/** The viewer's settings the data mirrors: DHPPD/NHPPD trainees, time groups, admins, time-off reasons, census grouping. */
	hppdIncludeTrainees: boolean;
	customTimeGroups: CustomTimeGroupsConfig;
	showAdmins: PrintoutShowAdminsConfig;
	includeTimeOffReason: boolean;
	censusGrouping: LocationMode;
	/** Per side: the viewer sees wages, the department isn't a hospital, and the day has wage figures for that side. */
	wagesVisible: { scheduled: boolean; actual: boolean };
	/** The day's CDPH census readings (`useCensusPointInTimesLoader`), null when there are none. */
	censusReadings: CensusPointInTimes | null;
	/** The viewer's language, for the written-out date and call times. */
	locale: string;
	dir: AiPrintoutData["dir"];
	labels: AiPrintoutLabels;
	callOffSourceLabel: (source: ChangesSource | null | undefined) => string;
};

/**
 * Builds the object a custom printout template renders, from the same inputs the Detailed printout uses.
 *
 * Deliberately has no Day view filter input: the set of people a custom printout must show is every shift
 * on the day, so the Detailed pipeline's own source rows run with every location selected and gaps on.
 * Row order is the Day view's own; templates sort with the provided sortable fields.
 */
export const buildAiPrintoutData = (input: BuildAiPrintoutDataInput): AiPrintoutData => {
	const build = createBuildContext(input);
	const { department } = input;
	return {
		contractVersion: CONTRACT_VERSION,
		date: build.scheduleDay.toFormat("yyyy-MM-dd"),
		dateLabel: build.scheduleDay.setLocale(input.locale).toLocaleString(DateTime.DATE_HUGE),
		isActual: input.isActual,
		department: {
			name: department.display_name,
			timezone: department.timezone,
			dayStart: department.dayStart,
			nameOrder: input.lastNameFirst ? "lastFirst" : "firstLast",
			usesJobs: input.jobManagementEnabled,
		},
		...buildRows(build),
		...buildDayMetrics(build),
		...buildDayExtras(build),
		labels: input.labels,
		dir: input.dir,
	};
};
